/**
 * Guide Command
 * Simple usage guide for Jai1 Framework (no TUI)
 */
import { Command } from 'commander';
import chalk from 'chalk';
import { getCliName } from '../utils/cli-name.js';

/**
 * Create guide command - plain text usage guide
 */
export function createGuideCommand(): Command {
  const cmd = new Command('guide')
    .description('Hướng dẫn sử dụng nhanh')
    .action(() => {
      const name = getCliName();
      showGuide(name);
    });

  return cmd;
}

function showGuide(name: string): void {
  console.log(chalk.cyan.bold('\n📖 Jai1 Framework - Hướng dẫn sử dụng nhanh\n'));

  // ── Setup & Auth ──
  console.log(chalk.bold('━━━ THIẾT LẬP ━━━'));
  console.log(`  ${chalk.cyan(`${name} auth`)}            Xác thực và cấu hình client`);
  console.log(`  ${chalk.cyan(`${name} status`)}          Hiển thị trạng thái cấu hình`);
  console.log(`  ${chalk.cyan(`${name} doctor`)}          Chuẩn đoán project hiện tại`);
  console.log();

  // ── Core Package ──
  console.log(chalk.bold('━━━ CORE PACKAGE ━━━'));
  console.log(`  ${chalk.cyan(`${name} apply`)}           Cài đặt components (interactive)`);
  console.log(`  ${chalk.cyan(`${name} apply core`)}      Cài core package (headless)`);
  console.log(`  ${chalk.cyan(`${name} check`)}           Kiểm tra cập nhật từ server`);
  console.log(`  ${chalk.cyan(`${name} update`)}          Cập nhật components đã cài`);
  console.log(`  ${chalk.cyan(`${name} upgrade`)}         Cập nhật CLI client`);
  console.log();
  console.log(chalk.dim('  Workflow phổ biến:'));
  console.log(chalk.dim(`    1. ${name} auth              # Đăng nhập lần đầu`));
  console.log(chalk.dim(`    2. ${name} apply core        # Cài core package`));
  console.log(chalk.dim(`    3. ${name} rules apply       # Cấu hình rules cho IDE`));
  console.log(chalk.dim(`    4. ${name} ide sync          # Đồng bộ sang IDE`));
  console.log(chalk.dim(`    5. ${name} check             # Kiểm tra cập nhật định kỳ`));
  console.log();

  // ── IDE ──
  console.log(chalk.bold('━━━ IDE ━━━'));
  console.log(`  ${chalk.cyan(`${name} ide status`)}      Các IDE được phát hiện`);
  console.log(`  ${chalk.cyan(`${name} ide setup`)}       Cấu hình IDE (VSCode optimizations)`);
  console.log(`  ${chalk.cyan(`${name} ide sync`)}        Đồng bộ .jai1 content đến IDE`);
  console.log(`  ${chalk.cyan(`${name} rules apply`)}     Cấu hình rules cho IDE`);
  console.log();

  // ── Skills ──
  console.log(chalk.bold('━━━ SKILLS ━━━'));
  console.log(`  ${chalk.cyan(`${name} s find`)}      ${chalk.dim('<query>')}                    Tìm skills trên server`);
  console.log(`  ${chalk.cyan(`${name} s find`)}      ${chalk.dim('<query> --skillsh')}           Tìm trên npm skills`);
  console.log(`  ${chalk.cyan(`${name} s add`)}       ${chalk.dim('<name>')}                      Cài skill vào .jai1/skills/`);
  console.log(`  ${chalk.cyan(`${name} s list`)}                                    Skills đã cài`);
  console.log(`  ${chalk.cyan(`${name} s list`)}      ${chalk.dim('--available')}                 Skills trên server`);
  console.log(`  ${chalk.cyan(`${name} s info`)}      ${chalk.dim('<name>')}                      Chi tiết skill`);
  console.log(`  ${chalk.cyan(`${name} s sync`)}      ${chalk.dim('--all -y')}                    Sync sang tất cả IDEs`);
  console.log();

  // ── Task Management ──
  console.log(chalk.bold('━━━ TASK MANAGEMENT ━━━'));
  console.log(`  ${chalk.cyan(`${name} t add`)}      ${chalk.dim('"title"')} ${chalk.dim('[-p 0-3] [-P parent]')}   Tạo task`);
  console.log(`  ${chalk.cyan(`${name} t list`)}     ${chalk.dim('[-s status] [-P parent]')}        Liệt kê tasks`);
  console.log(`  ${chalk.cyan(`${name} t ready`)}                                   Tasks sẵn sàng`);
  console.log(`  ${chalk.cyan(`${name} t pick`)}                                    Claim & bắt đầu`);
  console.log(`  ${chalk.cyan(`${name} t done`)}     ${chalk.dim('<id>')}                           Hoàn thành task`);
  console.log(`  ${chalk.cyan(`${name} t summary`)}                                 Dashboard tổng quan`);
  console.log(`  ${chalk.cyan(`${name} t guide`)}                                   Hướng dẫn chi tiết`);
  console.log();

  // ── Workflows ──
  console.log(chalk.bold('━━━ WORKFLOWS (dùng trong IDE) ━━━'));
  console.log(`  ${chalk.cyan('/improve')}          ${chalk.dim('[feature]')}   Phân tích & đề xuất cải thiện project`);
  console.log(`  ${chalk.cyan('/plan')}             ${chalk.dim('[task]')}      Lên kế hoạch nhanh → tasks → implement`);
  console.log(`  ${chalk.cyan('/develop-feature')}  ${chalk.dim('[feature]')}   Phát triển tính năng (FRD → TDD → Code)`);
  console.log(`  ${chalk.cyan('/fix-bug')}          ${chalk.dim('[bug]')}       Phân tích & sửa bug`);
  console.log(`  ${chalk.cyan('/commit-it')}                        Commit an toàn với message chi tiết`);
  console.log();
  console.log(chalk.dim('  Workflow cải thiện project:'));
  console.log(chalk.dim('    1. /improve                     # Phân tích & đề xuất'));
  console.log(chalk.dim('    2. Chọn improvements cần làm    # User xác nhận'));
  console.log(chalk.dim('    3. → Tự động gọi /plan          # Tạo tasks & implement'));
  console.log();

  // ── File Upload ──
  console.log(chalk.bold('━━━ FILE UPLOAD ━━━'));
  console.log(`  ${chalk.cyan(`${name} file upload`)} ${chalk.dim('<file>')}           Upload HTML/Markdown/Image, nhận URL`);
  console.log(`  ${chalk.cyan(`${name} file upload`)} ${chalk.dim('<file> --open')}    Upload & mở trên trình duyệt`);
  console.log(`  ${chalk.cyan(`${name} file upload`)} ${chalk.dim('<file> --personal')}Upload vào scope cá nhân`);
  console.log(`  ${chalk.cyan(`${name} file list`)}                      Danh sách files đã upload`);
  console.log(`  ${chalk.cyan(`${name} file list`)}   ${chalk.dim('--personal')}       Chỉ files cá nhân`);
  console.log(`  ${chalk.cyan(`${name} file open`)}   ${chalk.dim('<id>')}             Mở file trên trình duyệt`);
  console.log(`  ${chalk.cyan(`${name} file delete`)} ${chalk.dim('<id>')}             Xoá file`);
  console.log();
  console.log(chalk.dim('  Định dạng hỗ trợ: .html, .htm, .md, .markdown (tối đa 5MB)'));
  console.log(chalk.dim('  Markdown tự động convert sang HTML có style'));
  console.log(chalk.dim('  Auto-detect project từ .jai1/settings.json'));
  console.log();

  // ── AI Tools ──
  console.log(chalk.bold('━━━ AI TOOLS ━━━'));
  console.log(`  ${chalk.cyan(`${name} chat`)}            Chat AI qua Jai1 LLM Proxy`);
  console.log(`  ${chalk.cyan(`${name} translate`)}       Dịch văn bản/file bằng AI`);
  console.log(`  ${chalk.cyan(`${name} image`)}           Tạo ảnh bằng AI`);
  console.log();

  // ── Tips ──
  console.log(chalk.dim(`💡 Chạy "${name} <lệnh> --help" để xem chi tiết từng lệnh.`));
  console.log(chalk.dim(`💡 Chạy "${name} t guide" để xem hướng dẫn task management đầy đủ.`));
  console.log(chalk.dim(`💡 Chạy "${name} file" để xem hướng dẫn upload & share files.`));
  console.log(chalk.dim(`💡 Chạy "${name} quickstart" để xem hướng dẫn theo tình huống phát triển.`));
  console.log();
}
